import React, { useCallback, useEffect, useState } from 'react';
import { View, Platform } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAppStore } from './src/store/useAppStore';
import { checkSession, verifyMagicLink } from './src/services/auth/auth';
import type { AuthResult } from './src/schemas/auth/auth';
import { handoffToPlatform } from './src/lib/platformAuth';
import { useTheme } from './src/theme';
import Txt from './src/components/Txt';
import Button from './src/components/Button';
import AppShell from './src/navigation/AppShell';
import ToastHost from './src/components/toastHost';

/**
 * Cross-platform entry (native index.js + web index.web.ts via App.web.tsx re-export).
 * SK Hospitality owns no auth UI — the gate probes the platform session (/v1/me) and,
 * if there's none, hands off to skplatform-fe (a browser redirect on web, Linking on native).
 */

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 1 } },
});

function LoadingScreen({ message }: { message: string }) {
  const t = useTheme();
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: t.colors.paper }}>
      <Txt color="ink3">{message}</Txt>
    </View>
  );
}

/**
 * Shown when `/v1/me` fails with no HTTP response (platform down, DNS, CORS,
 * connection refused) — see the comment in AuthGate's catch below for why this
 * must be a dead-end retry screen, never an automatic handoffToPlatform().
 */
function UnreachableScreen({ onRetry }: { onRetry: () => void }) {
  const t = useTheme();
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: t.colors.paper, paddingHorizontal: 24 }}>
      <Txt size="lg" weight="semibold" style={{ marginBottom: 8, textAlign: 'center' }}>Can't reach the sign-in service</Txt>
      <Txt size="sm" color="ink3" style={{ textAlign: 'center', maxWidth: 380, marginBottom: 22 }}>
        We couldn't confirm your session — the authentication service didn't respond.
        This usually means a required backend isn't running. Your login is fine; try again once it's back up.
      </Txt>
      <Button variant="accent" onPress={onRetry}>Retry</Button>
    </View>
  );
}

function AuthGate() {
  const { isAuthenticated, login, logout } = useAppStore();
  // Seed the phase from the cached flag only to skip the loading flash — it's an
  // optimistic hint, not a gate. The effect below always re-verifies against the
  // live platform session, since the cached flag can't see a logout that happened
  // in another SK product (it only clears on a local sign-out in this app).
  const [phase, setPhase] = useState<'checking' | 'ready' | 'error' | 'unreachable'>(isAuthenticated ? 'ready' : 'checking');

  // A session that isn't fully onboarded yet (email unverified, or Step 2 —
  // org name/phone — still pending) isn't ready for the hosp dashboard.
  // SK Hospitality owns no auth/onboarding UI, so hand off back to the
  // platform to finish there instead of entering the app half-set-up.
  const enterIfComplete = useCallback((result: AuthResult, returnTo: string) => {
    // Flip phase away from 'ready' BEFORE navigating away — otherwise a stale
    // optimistic 'ready' (seeded from the cached flag) keeps AppShell mounted,
    // so its screens keep firing now-401ing requests for however long
    // window.location.assign() takes to actually unload the page.
    if (!result.emailVerified || !result.profileComplete) { setPhase('checking'); logout(); handoffToPlatform(returnTo); return; }
    login(result.token, result.role, result.profileId, result.firstName, result.displayName, result.email, result.orgId, result.orgName);
    setPhase('ready');
  }, [login, logout]);

  const runCheck = useCallback(() => {
    setPhase('checking');
    const returnTo = Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.href : '/';

    // Web magic-link landing: /auth/magic-verify?token=…
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location.pathname.startsWith('/auth/magic-verify')) {
      const token = new URLSearchParams(window.location.search).get('token');
      if (!token) { handoffToPlatform('/'); return; }
      verifyMagicLink(token)
        .then((result) => enterIfComplete(result, returnTo))
        .catch(() => { setPhase('error'); setTimeout(() => handoffToPlatform('/'), 2000); });
      return;
    }

    checkSession()
      .then((result) => enterIfComplete(result, returnTo))
      .catch((err) => {
        // CRITICAL: tell "auth backend unreachable" apart from "not logged in" —
        // same distinction SK Catalog's rootNavigator.tsx makes. A real HTTP
        // error response (401/403/5xx) means the platform genuinely rejected
        // us → hand off there. A transport failure (no `response` — platform
        // pod down, DNS, CORS, connection refused) must NOT be read as
        // "logged out": bouncing would send us to the platform which, if it
        // still holds a valid session, immediately redirects back here —
        // an infinite app<->platform redirect loop on a blank screen. Surface
        // an honest, retryable error instead.
        if (err?.response) { logout(); handoffToPlatform(returnTo); return; }
        setPhase('unreachable');
      });
  }, [enterIfComplete, logout]);

  useEffect(() => {
    runCheck();
    // Runs once on mount (plus manual Retry presses) — always re-verifies live,
    // regardless of the cached flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (phase === 'checking') return <LoadingScreen message="Loading…" />;
  if (phase === 'error') return <LoadingScreen message="Link expired. Redirecting…" />;
  if (phase === 'unreachable') return <UnreachableScreen onRetry={runCheck} />;
  return <AppShell />;
}

export default function App() {
  return (
    <SafeAreaProvider>
      <QueryClientProvider client={queryClient}>
        <AuthGate />
      </QueryClientProvider>
      {/* Global toast host (auto-selects toastHost.web.tsx on web) — above all content. */}
      <ToastHost />
    </SafeAreaProvider>
  );
}
